"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomMapOverlay = exports.CSS = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
const theme_1 = require("../../components/theme");
const mithril_1 = require("../../mithril");
const tag_state_1 = require("../../page/tag.state");
const services_1 = require("../../services");
const page_state_1 = require("../../state/page.state");
const store_1 = require("../../store");
const projection_cache_1 = require("../google/projection.cache");
const point_collection_1 = require("./point.collection");
const point_tag_1 = require("./point.tag");
const tag_map_state_1 = require("./tag.map.state");
exports.CSS = {
    container: 'c-MapOverlay',
    tag: 'c-MapOverlay__tag',
    line: 'c-MapOverlayLayer__line',
    point: 'c-MapOverlayLayer__point',
};
(0, theme_1.addStyles)({
    [exports.CSS.container]: {
        width: '100%',
        height: '100%',
    },
    [exports.CSS.tag]: {
        position: 'absolute',
    },
});
class CustomMapOverlay {
    constructor(state, map) {
        this.name = 'MapOverlay';
        this.overlay = new google.maps.OverlayView();
        this.state = state;
        this.setMap(map);
        this.updateMapBounds();
        this.overlay.onAdd = this.onAdd.bind(this);
        this.overlay.draw = this.onDraw.bind(this);
        this.overlay.onRemove = this.remove.bind(this);
        this.origin = new google.maps.LatLng(0, 0);
    }
    updateState() {
        this.state.setZoom(this.map.getZoom());
        this.state.setBounds(this.map.getBounds());
    }
    setMap(map) {
        this.map = map;
        if (map == null) {
            this.remove();
            if (this.centerReactionDisposer != null) {
                this.centerReactionDisposer();
                this.centerReactionDisposer = null;
            }
        }
        this.overlay.setMap(map);
    }
    onAdd() {
        this.el = this.overlay.getPanes().overlayMouseTarget;
        services_1.Services.Render.mount(this.el, this, this.name);
    }
    onDraw() {
        // Update state on map render tick to keep DOM in sync with map (events fire too late)
        this.updateState();
        const reset = this.verifyProjection();
        if (reset) {
            services_1.Services.Render.render(this.el, this.view());
        }
    }
    remove() {
        if (this.el) {
            services_1.Services.Render.mount(this.el, null, this.name);
            this.el = null;
        }
    }
    view() {
        const bounds = this.state.boundString.get();
        if (bounds == null || this.el == null || this.map == null) {
            return null;
        }
        this.verifyProjection();
        const visibleTags = this.state.visibleTags;
        if (visibleTags.length === 0) {
            return this.viewVisibleCollections();
        }
        if (this.state.zoom > tag_map_state_1.ZOOM_SWITCH_JOBS_LEVEL && visibleTags.length < 5) {
            return this.viewVisibleCollections();
        }
        if (this.state.zoom > tag_map_state_1.ZOOM_SWITCH_LEVEL) {
            return this.viewVisibleCollections();
        }
        return this.viewVisibleTags();
    }
    viewVisibleCollections() {
        const output = [];
        const nodesMap = {};
        for (const revision of this.state.visibleCollections) {
            const summary = store_1.AppStore.CollectionSummary.get(revision);
            if (summary == null) {
                continue;
            }
            if (summary == null || summary.location == null) {
                continue;
            }
            const [tagId] = summary.tags;
            let existing = nodesMap[tagId];
            if (existing == null) {
                existing = nodesMap[tagId] = {
                    lines: (0, mithril_1.m)('div', { key: 'layer-line-' + tagId, className: exports.CSS.line }, []),
                    points: (0, mithril_1.m)('div', { key: 'layer-point-' + tagId, 'c-id': tagId, className: exports.CSS.point }, []),
                };
                output.push(existing.lines);
            }
            const point = projection_cache_1.PointCache.getGeoPoint(summary.location);
            const points = point_collection_1.CollectionMapPoint.view(this.state, summary, point);
            if (points.collection) {
                existing.points.children.push(points.collection);
            }
            if (points.line) {
                const child = existing.lines.children;
                for (const line of points.line) {
                    child.push(line);
                }
            }
        }
        for (const key of Object.keys(nodesMap)) {
            const layers = nodesMap[key];
            output.push(layers.points);
        }
        return output;
    }
    viewVisibleTags() {
        const dedupe = {};
        const nodes = [];
        for (const tagId of this.state.visibleTags) {
            const tag = store_1.AppStore.Tag.get(tagId);
            nodes.push(this.viewTag(tag, dedupe));
        }
        // Place into render order (first child is rendered on bottom)
        return nodes.reverse();
    }
    viewTag(tag, dedupe) {
        const summary = store_1.AppStore.TagSummary.get(tag.objectId);
        if (summary == null || summary.center == null) {
            return null;
        }
        const point = projection_cache_1.PointCache.getPoint(summary.center.lat, summary.center.lng);
        const cacheKey = `${Math.floor(point.x)}:${Math.floor(point.y)}`;
        if (dedupe[cacheKey] && page_state_1.CurrentState.hoverId !== tag.objectId) {
            return null;
        }
        dedupe[cacheKey] = tag;
        return point_tag_1.TagMapPoint.view(tag, summary, point);
    }
    verifyProjection() {
        const projection = this.overlay.getProjection();
        if (projection == null) {
            projection_cache_1.PointCache.reset();
            return true;
        }
        projection_cache_1.PointCache.setProjection(projection);
        const point = projection.fromLatLngToDivPixel(this.origin);
        const oldPoint = projection_cache_1.PointCache.getPoint(this.origin.lat(), this.origin.lng());
        if (point.x !== oldPoint.x || point.y !== oldPoint.y) {
            projection_cache_1.PointCache.reset();
            return true;
        }
        return false;
    }
    updateMapBounds() {
        // Take bounds from URL
        if (this.state.bounds != null && this.state.zoom != null) {
            this.map.setCenter(this.state.bounds.getCenter());
            this.map.setZoom(this.state.zoom);
            return;
        }
        // Update bounds with collections when loaded
        this.centerReactionDisposer = (0, mobx_1.reaction)(() => this.state.tagState.fetchState, (fetchState) => {
            if (fetchState !== tag_state_1.TagFetchState.UNKNOWN) {
                this.state.centerMap();
            }
        }, { fireImmediately: true, delay: 100 });
    }
}
exports.CustomMapOverlay = CustomMapOverlay;
tslib_1.__decorate([
    mobx_1.action.bound
], CustomMapOverlay.prototype, "updateState", null);
tslib_1.__decorate([
    mobx_1.action
], CustomMapOverlay.prototype, "setMap", null);
//# sourceMappingURL=tag.map.overlay.js.map